import { Box } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { getAllServices, GetAllServicesSummary, Service } from "src/apis/services";
import PageContainer from "src/pages/components/PageContainer";
import PaymentStatusFilter, { PaymentFilterValue } from "src/pages/components/PaymentStatusFilter";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import ServicesTable from "src/pages/components/ServicesTable";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import SummaryFab from "src/pages/components/SummaryFab";
import { isUserAdministrator } from "src/utils/auth";
import { getDayFromWeekBefore, getToday } from "src/utils/date";
import AllServicesSummary from "./components/AllServicesSummary";

export const AllServicesPage: FC = () => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>(getDayFromWeekBefore());
  const [endDate, setEndDate] = useState<string>(getToday());
  const [services, setServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [summary, setSummary] = useState<GetAllServicesSummary|null>(null);
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilterValue>("all");

  const filterByPayment = useMemo(() => (
    (list: Service[], filter: PaymentFilterValue) => {
      if (filter === "paid") return list.filter((service) => service.pago === true);
      if (filter === "unpaid") return list.filter((service) => service.pago !== true);
      return list;
    }
  ), []);

  async function fetchServices() {
    setLoading(true)

    try {
      const res = await getAllServices(startDate, endDate)

      setAllServices(res.services)
      setServices(filterByPayment(res.services, paymentFilter))
      setSummary(res.summary)
    } catch (err: any) {
      setError(err?.error)
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setServices(filterByPayment(allServices, paymentFilter));
  }, [allServices, paymentFilter, filterByPayment])

  return (
    <PageContainer
      title="Todos os atendimentos"
      error={error}
      guard={isUserAdministrator}
    >
      {Boolean(summary) && (
        <SummaryFab>
          <AllServicesSummary
            startDate={startDate}
            endDate={endDate}
            summary={summary}
          />
        </SummaryFab>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "center",
          alignItems: "center",
          width: "100%"
        }}
      >
        <StartAndEndDateFilters
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <PaymentStatusFilter
          value={paymentFilter}
          onChange={setPaymentFilter}
        />
        <FilterButtons
          isLoading={loading}
          isDisabled={!startDate || !endDate}
          search={() => {
            fetchServices();
          }}
          resetState={() => {
            setServices([]);
            setAllServices([]);
            setSummary(null);
            setPaymentFilter("all");
          }}
        />
      </Box>
      <ServicesTable
        services={services}
        setError={setError}
      />
    </PageContainer>
  )
}